import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
// #region --------------- Contact React Query hooks ----------------------

import {
  createMatterStatusPhase,
  createMatterStatusV1,
  deleteMatterStatusV1,
  updateMatterStatusOrders,
  updateMatterStatusV1,
  type MatterSettings,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static";
import {
  type APIError,
  type APIResponse,
  type MatterStatusCategory,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import toast from "react-hot-toast";

const fallbackMatterStatusCategory = (
  categoryId: string,
): MatterStatusCategory => ({
  dateCreated: null,
  dateModified: null,
  description: null,
  id: categoryId,
  label: categoryId,
});

const getMatterStatusCategory = (
  queryClient: ReturnType<typeof useQueryClient>,
  categoryId: string,
) => {
  const categoriesData = queryClient.getQueryData<
    APIResponse<MatterStatusCategory[]>
  >([queryKeys.matterStatusCategories]);
  return (
    categoriesData?.data.find((category) => category.id === categoryId) ??
    fallbackMatterStatusCategory(categoryId)
  );
};

export const useCreateMatterStatus = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterStatusV1>[0]) =>
      createMatterStatusV1(data),
    onMutate: (data) => {
      const category = getMatterStatusCategory(queryClient, data.categoryId);
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              statuses: [
                ...oldData.data.statuses,
                {
                  ...data,
                  category,
                  dateCreated: new Date().toISOString(),
                  dateModified: new Date().toISOString(),
                  firmId: "",
                  id: "temp",
                },
              ],
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      if (showToast) {
        toast.error("Error creating matter status");
      }
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter status created successfully");
      }
    },
  });
};

export const useUpdateMatterStatus = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterStatusV1>[0]) =>
      updateMatterStatusV1(data),
    onMutate: (data) => {
      const category = data.categoryId
        ? getMatterStatusCategory(queryClient, data.categoryId)
        : undefined;
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;

          return {
            data: {
              ...oldData.data,
              statuses: oldData.data.statuses.map((group) =>
                group.id === data.id
                  ? {
                      ...group,
                      ...data,
                      category: category ?? group.category,
                      id: data.id,
                    }
                  : group,
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      if (showToast) {
        toastAPIErrorMessage(error, "Error updating matter status");
      }
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter status updated successfully");
      }
    },
  });
};

export const useDeleteMatterStatus = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterStatusV1>[0]) =>
      deleteMatterStatusV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              statuses: oldData.data.statuses.filter(
                (group) => group.id !== data.id,
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      if (showToast) {
        toastAPIErrorMessage(error, "Error deleting matter status");
      }
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter status deleted successfully");
      }
    },
  });
};

export const useUpdateMatterStatusOrder = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterStatusOrders>[0]) =>
      updateMatterStatusOrders(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData<
        APIResponse<MatterSettings>
      >([queryKeys.matterSettings]);
      const statusOrderById = new Map(
        data.statuses.map((status) => [status.id, status.order]),
      );

      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;

          const reorderedCategoryStatuses = oldData.data.statuses
            .filter((status) => status.categoryId === data.categoryId)
            .map((status) => ({
              ...status,
              order: statusOrderById.get(status.id) ?? status.order,
            }))
            .sort(
              (statusA, statusB) =>
                (statusA.order ?? Number.POSITIVE_INFINITY) -
                  (statusB.order ?? Number.POSITIVE_INFINITY) ||
                statusA.label.localeCompare(statusB.label) ||
                statusA.id.localeCompare(statusB.id),
            );
          let categoryStatusIndex = 0;

          return {
            ...oldData,
            data: {
              ...oldData.data,
              statuses: oldData.data.statuses.map((status) =>
                status.categoryId === data.categoryId
                  ? reorderedCategoryStatuses[categoryStatusIndex++]
                  : status,
              ),
            },
          };
        },
      );

      return { previousData };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      if (showToast) {
        toastAPIErrorMessage(error, "Error updating matter status");
      }
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter status updated successfully");
      }
    },
  });
};

export const useCreateMatterStatusPhase = ({
  invalidateOnSettled = true,
  showSuccessToast = true,
}: {
  invalidateOnSettled?: boolean;
  showSuccessToast?: boolean;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterStatusPhase>[0]) =>
      createMatterStatusPhase(data),
    onSettled: async () => {
      if (!invalidateOnSettled) {
        return;
      }
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterSettings],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      if (showSuccessToast) {
        toast.success("Matter status updated successfully");
      }
    },
    onError: (err) => {
      if (err instanceof AxiosError && err.status === 403) {
        toast.error("You do not have permission to update this matter status");
      } else {
        toast.error("Error updating matter status");
      }
    },
  });
};
